export type InterpreterPermissionRow = {
  languageFrom: string
  languageTo: string
  serviceTypeCodes: string[]
}

export type InterpreterPermissionContext = {
  hasPermissions: boolean
  rows: InterpreterPermissionRow[]
  allowedCommunicationTypeCodes: string[]
}

type MasterOption = {key: string; value: string}
export type SelectOption = {value: string; label: string}

const LEGACY_SUFFIX = ' (no longer permitted)'
const norm = (v?: string) => (v ?? '').trim().toLowerCase()
const eq = (a?: string, b?: string) => norm(a).length > 0 && norm(a) === norm(b)

export const normalizeInterpreterPermissionContext = (data: any): InterpreterPermissionContext => {
  const rows = (data?.rows ?? data?.Rows ?? []).map((r: any) => ({
    languageFrom: r.languageFrom ?? r.LanguageFrom,
    languageTo: r.languageTo ?? r.LanguageTo,
    serviceTypeCodes: (r.serviceTypeCodes ?? r.ServiceTypeCodes ?? []).map((c: string) =>
      String(c).trim()
    ),
  }))
  const allowedCommunicationTypeCodes = (
    data?.allowedCommunicationTypeCodes ??
    data?.AllowedCommunicationTypeCodes ??
    []
  ).map((c: string) => String(c).trim())
  return {
  hasPermissions: Boolean(
    data?.hasPermissions ??
      data?.HasPermissions ??
      (rows.length > 0 && allowedCommunicationTypeCodes.length > 0)
  ),
  rows,
  allowedCommunicationTypeCodes,
  }
}

export const buildSelectOptionsWithLegacy = (
  permitted: SelectOption[],
  current?: SelectOption[]
): SelectOption[] => {
  if (!current?.length) return permitted
  const out = permitted.slice()
  const seen = new Set(permitted.map((o) => norm(o.value)))
  for (const cur of current) {
    if (!cur?.value || seen.has(norm(cur.value))) continue
    seen.add(norm(cur.value))
    out.push({
      value: cur.value,
      label: cur.label?.includes(LEGACY_SUFFIX)
        ? cur.label
        : `${cur.label || cur.value}${LEGACY_SUFFIX}`,
    })
  }
  return out
}

export const getPermittedFromLanguageOptions = (
  ctx: InterpreterPermissionContext | undefined,
  languages: MasterOption[]
): SelectOption[] => {
  if (!ctx?.rows.length) return []
  const fromCodes = new Set(ctx.rows.map((r) => norm(r.languageFrom)))
  return languages
    .filter((l) => fromCodes.has(norm(String(l.key))))
    .map((l) => ({value: String(l.key), label: l.value}))
}

export const getPermittedToLanguageOptions = (
  ctx: InterpreterPermissionContext | undefined,
  languages: MasterOption[],
  languageFrom: string
): SelectOption[] => {
  if (!ctx?.rows.length || !languageFrom) return []
  const fromKey = norm(languageFrom)
  const toCodes = new Set<string>()
  for (const row of ctx.rows) {
    if (norm(row.languageFrom) === fromKey) toCodes.add(norm(row.languageTo))
  }
  return languages
    .filter((l) => toCodes.has(norm(String(l.key))))
    .map((l) => ({value: String(l.key), label: l.value}))
}

export const getPermittedServiceTypeOptions = (
  ctx: InterpreterPermissionContext | undefined,
  serviceTypes: MasterOption[],
  languageFrom: string,
  selectedTo: SelectOption[]
): SelectOption[] => {
  if (!ctx?.rows.length || !languageFrom || !selectedTo.length) return []

  const fromKey = norm(languageFrom)
  const toKeys = new Set(selectedTo.map((t) => norm(t.value)))
  const seen = new Set<string>()
  const out: SelectOption[] = []

  for (const row of ctx.rows) {
    if (norm(row.languageFrom) !== fromKey || !toKeys.has(norm(row.languageTo))) continue
    for (const code of row.serviceTypeCodes) {
      const key = norm(code)
      if (!key || seen.has(key)) continue
      seen.add(key)
      const master = serviceTypes.find((m) => eq(String(m.key), code))
      out.push({
        value: master ? String(master.key) : code.trim(),
        label: master?.value ?? code.trim(),
      })
    }
  }
  return out
}

export const getPermittedCommunicationTypeOptions = (
  ctx: InterpreterPermissionContext | undefined,
  communicationTypes: MasterOption[],
  languageFrom: string,
  selectedTo: SelectOption[],
  selectedService: SelectOption[]
): SelectOption[] => {
  if (!ctx?.allowedCommunicationTypeCodes.length || !languageFrom || !selectedTo.length || !selectedService.length) {
    return []
  }

  const fromKey = norm(languageFrom)
  const toKeys = new Set(selectedTo.map((t) => norm(t.value)))
  const svcKeys = new Set(selectedService.map((s) => norm(s.value)))

  let allowed = false
  for (const row of ctx.rows) {
    if (norm(row.languageFrom) !== fromKey || !toKeys.has(norm(row.languageTo))) continue
    for (const st of row.serviceTypeCodes) {
      if (svcKeys.has(norm(st))) {
        allowed = true
        break
      }
    }
    if (allowed) break
  }
  if (!allowed) return []

  const commSet = new Set(ctx.allowedCommunicationTypeCodes.map(norm))
  return communicationTypes
    .filter((c) => commSet.has(norm(String(c.key))))
    .map((c) => ({value: String(c.key), label: c.value}))
}

export const pickIfSingle = (options: SelectOption[]) => (options.length === 1 ? options : null)

export const applyInterpreterCascade = (args: {
  ctx: InterpreterPermissionContext
  languages: MasterOption[]
  serviceTypes: MasterOption[]
  communicationTypes: MasterOption[]
  setFieldValue: (field: string, value: any) => void
  nextFrom: string
  nextTo: SelectOption[]
  nextService: SelectOption[]
}) => {
  const {ctx, languages, serviceTypes, communicationTypes, setFieldValue, nextFrom, nextTo, nextService} =
    args
  const toPick = nextTo.length ? nextTo : pickIfSingle(getPermittedToLanguageOptions(ctx, languages, nextFrom)) ?? []
  const svcPick =
    nextService.length
      ? nextService
      : pickIfSingle(getPermittedServiceTypeOptions(ctx, serviceTypes, nextFrom, toPick)) ?? []
  const commPick = pickIfSingle(
    getPermittedCommunicationTypeOptions(ctx, communicationTypes, nextFrom, toPick, svcPick)
  )

  if (!nextTo.length && toPick.length) setFieldValue('fK_LanguageTo', toPick)
  if (!nextService.length && svcPick.length) setFieldValue('fK_ServiceType', svcPick)
  if (commPick) setFieldValue('fK_CommunicationType', commPick)
}
